/**
 * Go Deeper Scene
 *
 * The narrative pathway. "The Archivist" lore unfolds here.
 * Sequence: scramble reveal on home → video with voice-over → The Relic Bunker.
 */

import type { Scene } from '@/types/scene';
import { scrambleMorph } from '@/lib/scramble';
import { navigateTo } from '@/lib/scene';
import { duckAmbient, restoreAmbient } from '@/lib/audio';

/* ── Pre-navigation scramble (runs on home page) ─────────────── */

let introTimers: ReturnType<typeof setTimeout>[] = [];

/** Cancel any in-progress intro scramble (e.g. if user navigates away) */
export function cancelGoDeeperIntro(): void {
  for (const t of introTimers) clearTimeout(t);
  introTimers = [];
}

/**
 * Plays the "GO DEEPER?" → "WHO IS THE ARCHIVIST?" scramble
 * on the home page CTA, then navigates to the go-deeper scene.
 */
export function playGoDeeperIntro(): void {
  cancelGoDeeperIntro();

  const cta = document.querySelector<HTMLElement>('.go-deeper-cta');
  if (!cta) {
    void navigateTo('go-deeper');
    return;
  }

  // Stop the pulse animation during scramble
  cta.style.animation = 'none';

  scrambleMorph(
    cta,
    'GO DEEPER?',
    'WHO IS THE ARCHIVIST?',
    introTimers,
    () => {
      // Hold the reveal, then fade out and navigate
      const holdTimer = setTimeout(() => {
        cta.style.transition = 'opacity 800ms ease-out';
        cta.style.opacity = '0';

        const navTimer = setTimeout(() => {
          void navigateTo('go-deeper');
        }, 800);
        introTimers.push(navTimer);
      }, 1000);
      introTimers.push(holdTimer);
    },
    { scrambleDuration: 2400, chaosHold: 600, resolveWindow: 1600 }
  );
}

/* ── Video scene state & lifecycle ────────────────────────────── */

interface GoDeeperState {
  timers: ReturnType<typeof setTimeout>[];
  videoEndHandler: (() => void) | null;
  videoPlayHandler: (() => void) | null;
  videoPauseHandler: (() => void) | null;
}

let state: GoDeeperState | null = null;

export function initGoDeeper(): void {
  state = {
    timers: [],
    videoEndHandler: null,
    videoPlayHandler: null,
    videoPauseHandler: null,
  };

  const video = document.querySelector<HTMLVideoElement>('.deeper-video');
  const bunkerCta = document.querySelector<HTMLElement>('.deeper-bunker-cta');

  if (video) {
    // Duck ambient while video plays
    const onPlay = (): void => {
      duckAmbient();
    };
    const onPause = (): void => {
      restoreAmbient();
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    state.videoPlayHandler = onPlay;
    state.videoPauseHandler = onPause;

    // Auto-play after a brief settle
    const playTimer = setTimeout(() => {
      video.play().catch(() => {
        // Autoplay blocked — show CTA as fallback
        showBunkerCta(bunkerCta);
      });
    }, 500);
    state.timers.push(playTimer);

    // Show CTA when video ends
    const onEnded = (): void => {
      restoreAmbient();
      showBunkerCta(bunkerCta);
    };
    video.addEventListener('ended', onEnded);
    state.videoEndHandler = onEnded;
  } else {
    // No video available — show CTA after a delay
    const t = setTimeout(() => {
      showBunkerCta(bunkerCta);
    }, 1000);
    state.timers.push(t);
  }
}

function showBunkerCta(el: HTMLElement | null): void {
  if (!el) return;
  el.style.display = '';
  const t = setTimeout(() => {
    el.classList.add('visible');
  }, 300);
  state?.timers.push(t);
}

export function destroyGoDeeper(): void {
  if (!state) return;
  for (const t of state.timers) clearTimeout(t);

  const video = document.querySelector<HTMLVideoElement>('.deeper-video');
  if (video) {
    if (state.videoEndHandler) video.removeEventListener('ended', state.videoEndHandler);
    if (state.videoPlayHandler) video.removeEventListener('play', state.videoPlayHandler);
    if (state.videoPauseHandler) video.removeEventListener('pause', state.videoPauseHandler);
    video.pause();
  }
  restoreAmbient();
  state = null;
}

/* ── Scene definition ────────────────────────────────────────── */

export const goDeeper: Scene = {
  id: 'go-deeper',
  name: 'Go Deeper',
  atmosphere: {
    background: 'linear-gradient(180deg, #050508 0%, #0a0a1a 50%, #0f0510 100%)',
    fogIntensity: 0.4,
    noiseHue: 330,
    noiseBrightness: 0.8,
    noiseOpacity: 0.6,
    // noiseFilter: 'ethereal',
  },
  interactions: [
    {
      id: 'back-home',
      target: '[data-interaction="back-home"]',
      action: { type: 'navigate', sceneId: 'home' },
    },
    {
      id: 'nav-music',
      target: '[data-interaction="nav-music"]',
      action: { type: 'navigate', sceneId: 'music' },
    },
    {
      id: 'nav-drip',
      target: '[data-interaction="nav-drip"]',
      action: { type: 'navigate', sceneId: 'drip' },
    },
    {
      id: 'nav-more',
      target: '[data-interaction="nav-more"]',
      action: { type: 'navigate', sceneId: 'more' },
    },
    {
      id: 'enter-bunker',
      target: '[data-interaction="enter-bunker"]',
      action: { type: 'navigate', sceneId: 'relic-bunker' },
    },
  ],
  transitions: [
    { to: 'home', effect: 'fade', duration: 800 },
    { to: 'music', effect: 'fade', duration: 800 },
    { to: 'drip', effect: 'fade', duration: 800 },
    { to: 'more', effect: 'fade', duration: 800 },
    { to: 'relic-bunker', effect: 'fade', duration: 1200 },
  ],
  render: () => `
    <div class="pathway-content go-deeper-content">
      <div class="deeper-video-frame">
        <video class="deeper-video" src="https://pub-1d02f4e0b0b64668be13195dd1fc7a96.r2.dev/video/archivist-reveal.mp4" preload="auto" playsinline></video>
      </div>
      <!-- Relic Bunker CTA hidden until bunker is ready -->
      <!-- <span class="deeper-bunker-cta" data-interaction="enter-bunker" style="display:none">THE RELIC BUNKER</span> -->
    </div>
  `,
};
